/**
 * The pure simulation behind <Confetti />, kept free of React and canvas so it
 * can be exercised by plain `node --test` (the client carries no jsdom or
 * testing-library, and this is not worth adding them for).
 *
 * The component owns rendering; this owns "where is each piece, and is the burst
 * over yet".
 */

export const COLORS = [
  '#00C49A', // emerald-500
  '#26FFB5', // emerald-400
  '#4DFFC3', // emerald-300
  '#F4A623', // gold-500
  '#F9C847', // gold-400
  '#FDE099', // gold-200
  '#4E6FAF', // navy-400
  '#FFFFFF',
];

export const GRAVITY = 0.12;
export const DRAG = 0.994;
export const FADE_START = 0.7; // fraction of life after which a piece starts fading

// Bounds the burst: every piece dies by this many frames, so the animation loop
// is guaranteed to terminate rather than spin forever on a mounted component.
export const MAX_LIFE_FRAMES = 180;

/**
 * Two angled jets from the lower corners, the way a real confetti cannon fires.
 * A single centre burst reads as a screensaver; the crossing streams read as a
 * celebration aimed at the card in the middle.
 *
 * @param rand injectable for deterministic tests; defaults to Math.random
 */
export const makePieces = (width, height, count, rand = Math.random) => {
  const pieces = [];
  const origins = [
    { x: width * 0.08, y: height * 0.92, angle: -Math.PI / 3.1 },
    { x: width * 0.92, y: height * 0.92, angle: -Math.PI + Math.PI / 3.1 },
  ];

  for (let i = 0; i < count; i += 1) {
    const origin = origins[i % origins.length];
    // Spread and speed are randomised per piece so the jet has body instead of
    // looking like a single rigid line of dots.
    const spread = (rand() - 0.5) * 0.9;
    const angle = origin.angle + spread;
    const speed = 9 + rand() * 9;

    pieces.push({
      x: origin.x,
      y: origin.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + rand() * 6,
      color: COLORS[Math.floor(rand() * COLORS.length)],
      rotation: rand() * Math.PI * 2,
      spin: (rand() - 0.5) * 0.28,
      // Ribbons tumble differently from squares; mixing both stops the burst
      // looking like uniform pixels.
      ribbon: rand() < 0.45,
      life: 0,
      maxLife: 110 + rand() * (MAX_LIFE_FRAMES - 110),
    });
  }
  return pieces;
};

/** Advance one piece by a frame, in place. */
export const stepPiece = (p) => {
  p.life += 1;
  p.vx *= DRAG;
  p.vy = p.vy * DRAG + GRAVITY;
  p.x += p.vx;
  p.y += p.vy;
  p.rotation += p.spin;
  return p;
};

export const isDead = (p) => p.life >= p.maxLife;

/** 1 while the piece is fully opaque, easing to 0 at the end of its life. */
export const alphaFor = (p) => {
  const progress = p.life / p.maxLife;
  if (progress < FADE_START) return 1;
  return Math.max(0, 1 - (progress - FADE_START) / (1 - FADE_START));
};

/**
 * Advance every piece and report how many are still alive. Zero means the burst
 * is finished and the caller should stop requesting frames.
 */
export const stepAll = (pieces) => {
  let alive = 0;
  for (const p of pieces) {
    if (isDead(p)) continue;
    stepPiece(p);
    alive += 1;
  }
  return alive;
};
